import { Dispatch, SetStateAction, MouseEvent, ChangeEvent, useState, useEffect } from "react";
import { ACPredictDAGRequestModel, Dataset } from "../../../../api/models";
import InputField from "../../../../components/Form/InputField";
import Buttons from "../../../../components/Buttons/Buttons";

import { ReactComponent as AutogenerateIcon } from '../../../../assets/Icons/adornments/Generate.svg';
import SelectField from "../../../../components/Form/SelectFields";
import ConditionalSelectField from "../../../../components/Form/ConditionSelectField";
import { classificationMethods, regressionMethods } from "../../../../utils/supplementalData";
import TagsField from "../../../../components/Form/TagsField";
import BooleanField from "../../../../components/Form/BooleanField";

const predTypeOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'conformal-prediction', label: 'Conformal Prediction' }
]

const modelTypeOptions = classificationMethods.concat(regressionMethods).map(method => ({ value: method, label: method }));

const averagingModeOptions = [
    { value: 'All', label: 'All' },
    { value: 'Individual', label: 'Individual' }
];

export default function SetPredictionParameters({
    form,
    setForm,
    chosenDS,
    showSelect,
    modelTypes,
    setModelTypes,
    numSelectedModels,
}: {
    form: ACPredictDAGRequestModel,
    setForm: Dispatch<SetStateAction<ACPredictDAGRequestModel>>,
    chosenDS: Dataset | null,
    showSelect: boolean,
    modelTypes: string[], 
    setModelTypes: Dispatch<SetStateAction<string[]>>,
    numSelectedModels: number,
}) {
    const [ selectedAveragingMode, setSelectedAveragingMode ] = useState<'All' | 'Individual'>('All');
    const [ fieldOptions, setFieldsOptions ] = useState<{ value: string, label: string }[]>([]);

    useEffect(() => {
        if (chosenDS) {
            const columnMetadataKey = Object.keys(chosenDS.dataset_metadata!.column_metadata!);
            if (columnMetadataKey.length > 0) {
                setFieldsOptions(columnMetadataKey.map(key => ({ value: key, label: key })))
            }
        }
    }, [chosenDS])

    const autoGenerateName = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (chosenDS) {
            const name = chosenDS.name!;
            setForm({
                ...form,
                name,
            })
        }
    };

    const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        })
    };

    const handleSelect = (e: any, name: string) => {
        setForm({
            ...form,
            [name]: Array.isArray(e) ? e.map(val => val.value) : e.value,
        })
    }

    const handleSelectAveragingMode = (e:any, name: string) => {
        const value = e.value as 'All' | 'Individual';
        setSelectedAveragingMode(value);

        setModelTypes([]);
    };

    const handleSelectModels = (e:any, name: string) => {
        const value = e as { value: string, label: string }[];
        setModelTypes(value.map(val => val.value));
    }

    const handleBoolean = (e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;

        setForm({
            ...form,
            perform_evaluation: checked,
            dataset: {
                ...form.dataset!,
                y_col: '',
            }
        });
    };

    const handleExplainabilityToggle = (e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setForm({
            ...form,
            molecule_highlighting: checked,
            explainability_enabled: checked,
        });
    };

    const handleSelectYCol = (e: any, name: string) => {
        const value = e.value as string;
        setForm({
            ...form,
            dataset: {
                ...form.dataset!,
                y_col: value,
            }
        })
    }

    const handleAddTag = (category: string, tag: string) => {
        let addedTag = '';

        if (category === 'general') {
            addedTag = tag;
        } else {
            addedTag = `${category}::${tag}`
        }

        setForm({
            ...form,
            tags: form!.tags!.concat(addedTag)

        })
    }

    const handleRemoveTag = (index: number) => {
        setForm({
            ...form,
            tags: form!.tags!.filter((t, i) => i !== index)
        })
    }


    return (
        <>
            <div className="w-full flex justify-between items-end gap-4">
                <InputField 
                    label="Resultset Name"
                    type='text'
                    id='name'
                    name='name'
                    value={form.name as string}
                    isRequired
                    handleChange={handleInput}
                />
                <Buttons
                    id='autogenerate-btn'
                    type='secondary'
                    styling="solid"
                    label='Autogenerate'
                    startingAdornment={<AutogenerateIcon/>}
                    action={autoGenerateName}
                />
            </div>

            <div className="w-full">
                <ConditionalSelectField label="Averaging Mode" showSelect={showSelect}>
                    <SelectField
                        label="Averaging Mode"
                        id='averaging_mode'
                        name='averaging_mode'
                        options={averagingModeOptions}
                        value={{ value: selectedAveragingMode, label: selectedAveragingMode }}
                        isRequired
                        handleChange={handleSelectAveragingMode}
                        isCreatable={false}
                        isMulti={false}
                        menuPortalTarget={document.getElementById('batch-pred-popup')}
                    />
                </ConditionalSelectField>
            </div>

            {selectedAveragingMode === 'Individual' && (
                <div className="w-full">
                    <ConditionalSelectField label="Models" showSelect={showSelect}>
                        <SelectField
                            label="Models"
                            id='models'
                            name='models'
                            options={modelTypeOptions}
                            value={modelTypes.map(type => ({ value: type, label: type }))}
                            isRequired
                            handleChange={handleSelectModels}
                            isCreatable={false}
                            isMulti
                            menuPortalTarget={document.getElementById('batch-pred-popup')}
                        />
                    </ConditionalSelectField>
                </div>
            )}

            <div className="w-full">
                <ConditionalSelectField label='Prediction Type' showSelect={showSelect}>
                    <SelectField
                        label="Prediction Type"
                        id='prediction_type'
                        name='prediction_type'
                        options={predTypeOptions}
                        value={{ value: form.prediction_type!, label: predTypeOptions.find(opt => opt.value === form.prediction_type!)!.label }}
                        isRequired
                        handleChange={handleSelect}
                        isCreatable={false}
                        isMulti={false}
                        menuPortalTarget={document.getElementById('batch-pred-popup')}
                    />
                </ConditionalSelectField>
            </div>

            <div className="w-full flex justify-start items-end gap-4">
                <div className="w-1/2">
                    <BooleanField 
                        label="Perform Validation"
                        id="perform_evaluation"
                        name='perform_evaluation'
                        value={form.perform_evaluation!}
                        handleChange={handleBoolean}
                        isRequired={false}
                    />
                </div>

                {form.perform_evaluation && (
                    <div className="w-1/2">
                        <SelectField
                            label="Known Value Column"
                            id='y_col'
                            name='y_col'
                            options={fieldOptions}
                            value={form.dataset!.y_col ? { value: form.dataset!.y_col, label: form.dataset!.y_col } : null}
                            isRequired
                            handleChange={handleSelectYCol}
                            isCreatable={false}
                            isMulti={false}
                            menuPortalTarget={document.getElementById('batch-pred-popup')}
                        />
                    </div>
                )}
            </div>

            <TagsField 
                label="Resultset"
                tags={form.tags! as string[]}
                showSelect={showSelect}
                menuPortalTarget={document.getElementById('batch-pred-popup')}
                handleAddTag={handleAddTag}
                handleRemoveTag={handleRemoveTag}
            />

            <div className="w-full flex flex-col gap-1">
                <BooleanField 
                    label="Run Explainability & Molecule Highlighting"
                    id="explainability"
                    name="explainability"
                    value={form.molecule_highlighting ?? false}
                    handleChange={handleExplainabilityToggle}
                    isRequired={false}
                />
                <p className="text-xs text-gray-500 ml-1">
                    Generates per-atom/bond importance highlighting and SHAP-based feature ranking. May increase prediction time for large workloads.
                </p>
            </div>

            {form.molecule_highlighting && (numSelectedModels > 5 || (chosenDS?.dataset_metadata?.records_number ?? 0) > 20) && (
                <div className="w-full rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    ⚠️ Explainability on large workloads may take significantly longer.
                    {numSelectedModels > 5 && <span> You have <strong>{numSelectedModels}</strong> model families selected.</span>}
                    {(chosenDS?.dataset_metadata?.records_number ?? 0) > 20 && <span> Your dataset has <strong>{chosenDS?.dataset_metadata?.records_number}</strong> molecules.</span>}
                    {' '}Consider reducing models or dataset size for faster results.
                </div>
            )}
        </>
    )
}